import {
  Alert,
  AlertTitle,
  Avatar,
  Box,
  GridLegacy,
  Stack,
  Typography,
} from '@mui/material';
import { HookFormInput } from 'ui-component/HookFormComponents';
import { OrgPartFormConstants } from 'ui-component/CreateNewOrgPartDialog/constants';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import FileUploadInput from 'ui-component/FileUploadInput';
import {
  useGetPublicManufacturersQuery,
  useScrapeOrgPartMutation,
} from 'store/slices/clientV2/parts';
import { PublicManufacturerSchema } from 'types/part';
import { ALL, DISABLE_PAGE_LIMIT, QueryFilterOperators } from 'types/api';
import { IconSparkles } from '@tabler/icons-react';
import Chip from 'ui-component/extended/Chip';
import SegmentLoadingButton from 'ui-component/Segment/SegmentLoadingButton';
import { useAsyncRequest, RequestPersistenceType } from 'hooks/useAsyncRequest';
import useSnackbar from 'hooks/useSnackbar';
import { handleErr } from 'utils/functions';
import { OrgPartScrapeResponseSchema, OrgPartType } from 'types/clientV2/parts';
import { gridSpacing } from 'constants/themeConstants';
import _ from 'lodash';
import { useTheme } from '@mui/material/styles';
import { PaddedDivider } from 'ui-component/CreateNewOrgPartDialog/components/CreatePartForm';
import { FeatureFlags } from 'types';
import { useFeature } from '@growthbook/growthbook-react';
import {
  UNLIMITED_QUANTITY,
  UNLIMITED_QUANTITY_STRING,
} from 'constants/procurementConstants';
import useServerSideQuery from 'hooks/useServerSideQuery';
import { OrgSupplier, OrgSupplierLite } from 'types/suppliers';
import {
  useGetOrgSuppliersServerSideQuery,
  useLazyGetOrgSuppliersServerSideQuery,
} from 'store/slices/apiV1/suppliers';
import { HookFormMultiQuerySingleFieldSearchSelect } from 'ui-component/HookFormComponents/HookFormMultiQuerySingleFieldSearchSelect';
import {
  UnitOfMeasureDetails,
  UnitOfMeasureOptions,
} from 'types/unitsOfMeasure';
import LoadingAlert from 'ui-component/extended/LoadingAlert';
import { stripUrlToDomain } from 'utils/urls';
import { useServerSideLazyQuery } from 'hooks/useServerSideLazyQuery';

export type MatchOrgSupplierSubSchema = Pick<
  OrgSupplier,
  | 'id'
  | 'name'
  | 'manufacturerMatchRegex'
  | 'autoScrape'
  | 'productPatchUrl'
  | 'mpnMatchRegex'
>;

type PartDetailsFormProps = {
  isManualEntry?: boolean;
  formRenderMode: OrgPartType;
  setHeroImage: (file: File | null) => void;
  searchPublicPart: (mpn: string, mfg?: string) => Promise<void>;
  hideLink?: boolean;
};

const PartDetailsForm = ({
  isManualEntry = false,
  formRenderMode,
  setHeroImage,
  searchPublicPart,
  hideLink = false,
}: PartDetailsFormProps) => {
  const tempScrapePart = useFeature(FeatureFlags.tempScrapePart).on;
  const { dispatchErrorSnackbar } = useSnackbar();
  const [scrapedUrl, setScrapedUrl] = useState<string | null>(null);
  const theme = useTheme();
  const {
    control,
    setValue,
    reset,
    getValues,
    formState: { errors },
  } = useFormContext();

  const [scrapeOrgPart, { isFetching: isLoadingScrapeOrgPart, jobMessage }] =
    useAsyncRequest(useScrapeOrgPartMutation, RequestPersistenceType.component);

  const [getLinkOrgSupplier] = useServerSideLazyQuery<
    OrgSupplier,
    OrgSupplierLite
  >(useLazyGetOrgSuppliersServerSideQuery);

  const showHero = isManualEntry && !(formRenderMode === OrgPartType.SERVICE);
  const showLink =
    !hideLink &&
    _.includes([OrgPartType.OTS, OrgPartType.NON_STOCKED], formRenderMode);

  const watchMfg = useWatch({
    control,
    name: OrgPartFormConstants.mfg.id,
  });
  const watchMpn = useWatch({
    control,
    name: OrgPartFormConstants.mpn.id,
  });

  const watchLink = useWatch({
    control,
    name: OrgPartFormConstants.link.id,
  });

  const watchHeroImageUrl = useWatch({
    control,
    name: OrgPartFormConstants.heroImageUrl.id,
  });

  const watchScrapedFields = useWatch({
    control,
    name: OrgPartFormConstants.scrapedFields.id,
  });

  // query org suppliers to guess the matching org supplier by manufacturer match regex
  const { data: matchOrgSuppliers } = useServerSideQuery<
    OrgSupplier,
    MatchOrgSupplierSubSchema
  >(useGetOrgSuppliersServerSideQuery, {
    pageSize: DISABLE_PAGE_LIMIT,
    schema: [
      'id',
      'name',
      'manufacturerMatchRegex',
      'autoScrape',
      'productPatchUrl',
      'mpnMatchRegex',
    ],
    filters: [
      {
        field: 'manufacturerMatchRegex',
        operator: QueryFilterOperators.isNotEmpty,
        value: true,
      },
    ],
  });

  useEffect(() => {
    if (watchMfg) {
      const matchOrgSupplier = matchOrgSuppliers?.data?.find((supplier) => {
        if (!supplier.manufacturerMatchRegex) return false;
        try {
          const regex = new RegExp(supplier.manufacturerMatchRegex, 'i');
          return regex.test(watchMfg ?? '');
        } catch {
          return false;
        }
      });
      if (
        matchOrgSupplier?.autoScrape &&
        matchOrgSupplier?.productPatchUrl &&
        watchMpn
      ) {
        setValue(
          OrgPartFormConstants.link.id,
          matchOrgSupplier?.productPatchUrl?.replace(
            '{{mpn}}',
            watchMpn ?? ''
          ) ?? null
        );
      }
      if (matchOrgSupplier) {
        setValue(
          OrgPartFormConstants.orgSupplier.id,
          matchOrgSupplier?.id ?? null
        );
      }
    }
  }, [watchMfg, matchOrgSuppliers, setValue, watchMpn]);

  const handleSearchBlur = async () => {
    if (
      watchMpn &&
      !_.includes(watchScrapedFields, OrgPartFormConstants.mpn.id)
    ) {
      await searchPublicPart(watchMpn, watchMfg ?? undefined);
    }
  };

  const handleScrapePart = async () => {
    if (watchLink) {
      try {
        const response = (await scrapeOrgPart({
          url: watchLink,
          includePartData: true,
          includeSupplierQuoteData: true,
        })) as OrgPartScrapeResponseSchema;

        const linkOrgSupplier = await getLinkOrgSupplier({
          pageSize: 1,
          schema: [ALL],
          filters: [
            {
              field: 'website',
              operator: QueryFilterOperators.contains,
              value: stripUrlToDomain(watchLink),
            },
          ],
        });

        setScrapedUrl(watchLink);

        const formUpdates = {
          [OrgPartFormConstants.linkMatchedPart.id]: false,
          [OrgPartFormConstants.scrapedFields.id]: [
            ..._.chain(response.partData)
              .pickBy((value) => !!value)
              .keys()
              .map((k) =>
                _.get(
                  {
                    manufacturer: OrgPartFormConstants.mfg.id,
                    mpn: OrgPartFormConstants.mpn.id,
                    description: OrgPartFormConstants.description.id,
                    specs: OrgPartFormConstants.specs.id,
                    isElectronicComponent:
                      OrgPartFormConstants.isElectronicComponent.id,
                    terminationType: OrgPartFormConstants.mountType.id,
                    package: OrgPartFormConstants.packageType.id,
                    msl: OrgPartFormConstants.msl.id,
                    heroImageUrl: OrgPartFormConstants.heroImageUrl.id,
                    dimensionClass: OrgPartFormConstants.dimensionClass.id,
                    preferredUnit: OrgPartFormConstants.preferredUnit.id,
                  },
                  k,
                  null
                )
              )
              .compact()
              .value(),
            ..._.chain(response.supplierQuoteData)
              .pickBy((value) => !!value)
              .keys()
              .map((k) =>
                _.get(
                  {
                    shipsInLead: OrgPartFormConstants.shipsInLead.id,
                    sku: OrgPartFormConstants.sku.id,
                    packaging: OrgPartFormConstants.packaging.id,
                    coa: OrgPartFormConstants.coa.id,
                    moq: OrgPartFormConstants.moq.id,
                    multiple: OrgPartFormConstants.multiple.id,
                    maximum: OrgPartFormConstants.maximum.id,
                    prices: OrgPartFormConstants.prices.id,
                    dimensionUnit: OrgPartFormConstants.dimensionUnit.id,
                    dimensionClass: OrgPartFormConstants.dimensionClass.id,
                    dimensionValue: OrgPartFormConstants.dimensionValue.id,
                  },
                  k,
                  null
                )
              )
              .compact()
              .value(),
          ],
          [OrgPartFormConstants.stockDocument.id]: response.stockDocument,
          [OrgPartFormConstants.mfg.id]:
            response.partData.manufacturer ||
            _.first(linkOrgSupplier?.data?.data)?.name,
          [OrgPartFormConstants.mpn.id]: response.partData.mpn,
          [OrgPartFormConstants.preferredUnit.id]:
            response.partData.preferredUnit,
          [OrgPartFormConstants.description.id]: response.partData.description,
          [OrgPartFormConstants.specs.id]: _.chain(response.partData.specs)
            .filter((spec) => !!spec.value)
            .keyBy('label')
            .mapValues('value')
            .value(),
          [OrgPartFormConstants.isElectronicComponent.id]:
            response.partData.isElectronicComponent,
          [OrgPartFormConstants.mountType.id]:
            response.partData.terminationType,
          [OrgPartFormConstants.packageType.id]: response.partData.package,
          [OrgPartFormConstants.msl.id]: response.partData.msl,
          [OrgPartFormConstants.heroImageUrl.id]:
            response.partData.heroImageUrl,
          [OrgPartFormConstants.supplierQuoteOpen.id]:
            !!response.supplierQuoteData.prices,
          [OrgPartFormConstants.orgSupplier.id]:
            _.first(linkOrgSupplier?.data?.data)?.id ?? null,
          [OrgPartFormConstants.shipsInLead.id]:
            response.supplierQuoteData.shipsInLead,
          [OrgPartFormConstants.sku.id]: response.supplierQuoteData.sku,
          [OrgPartFormConstants.packaging.id]:
            response.supplierQuoteData.packaging,
          [OrgPartFormConstants.coa.id]: response.supplierQuoteData.coa,
          [OrgPartFormConstants.moq.id]:
            _.toNumber(response.supplierQuoteData.moq) || 1,
          [OrgPartFormConstants.multiple.id]:
            _.toNumber(response.supplierQuoteData.multiple) ||
            _.toNumber(response.supplierQuoteData.packageQuantity) ||
            1,
          [OrgPartFormConstants.maximum.id]:
            _.toNumber(response.supplierQuoteData.maximum) ===
            UNLIMITED_QUANTITY
              ? UNLIMITED_QUANTITY_STRING
              : _.toNumber(response.supplierQuoteData.maximum) ||
                UNLIMITED_QUANTITY_STRING,
          [OrgPartFormConstants.dimensionUnit.id]:
            response.supplierQuoteData.dimensionUnit ||
            response.partData.preferredUnit ||
            UnitOfMeasureOptions.PIECE,
          [OrgPartFormConstants.dimensionClass.id]:
            UnitOfMeasureDetails[
              response.supplierQuoteData.dimensionUnit ||
                response.partData.preferredUnit ||
                UnitOfMeasureOptions.PIECE
            ].dimensionClass,
          [OrgPartFormConstants.dimensionValue.id]:
            response.supplierQuoteData.packageQuantity || 1,
          [OrgPartFormConstants.prices.id]: _.map(
            response.supplierQuoteData.prices || [],
            (price) => ({
              to: price.from,
              unitPrice: price.unitPrice,
            })
          ),
        };

        reset(
          {
            ...getValues(),
            ...formUpdates,
          },
          {
            keepDefaultValues: true,
            keepDirty: true,
            keepErrors: true,
            keepIsSubmitted: true,
            keepTouched: true,
          }
        );
      } catch (err) {
        handleErr(err, (errMessage: string) => {
          dispatchErrorSnackbar(errMessage);
        });
      }
    }
  };

  return (
    <GridLegacy
      container
      spacing={showHero ? 2 : 0}
      direction="row"
      justifyContent="stretch"
      alignItems="stretch"
      sx={{ width: '100%', height: '100%' }}
    >
      {showHero && !watchHeroImageUrl && (
        <GridLegacy item xs={3}>
          <FileUploadInput
            batchFileMode={false}
            onChange={setHeroImage}
            acceptedExtensions={['.jpg', '.jpeg', '.png']}
            defaultText="Upload Image"
          />
        </GridLegacy>
      )}
      {!!watchHeroImageUrl && (
        <GridLegacy item xs="auto" sx={{ position: 'relative' }}>
          <Avatar
            src={watchHeroImageUrl}
            variant="rounded"
            sx={{
              width: '187px',
              height: '187px',
              mr: gridSpacing,
              '& img': {
                objectFit: 'contain',
              },
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: 20,
              left: 20,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          >
            <IconSparkles color={theme.palette.success.main} size={20} />
          </Box>
        </GridLegacy>
      )}
      <GridLegacy item xs>
        <Stack spacing={2}>
          {showLink && (
            <HookFormInput
              control={control}
              errors={errors}
              name={OrgPartFormConstants.link.id}
              label={OrgPartFormConstants.link.label}
              boxSx={{ mb: 0 }}
            />
          )}
          {(watchLink !== scrapedUrl || !scrapedUrl || !watchLink) &&
            tempScrapePart &&
            showLink &&
            !isLoadingScrapeOrgPart && (
              <Alert
                severity="success"
                variant="standard"
                icon={false}
                action={
                  <SegmentLoadingButton
                    onClick={handleScrapePart}
                    variant="contained"
                    loading={isLoadingScrapeOrgPart}
                    disabled={!watchLink}
                  >
                    AI Analyze
                  </SegmentLoadingButton>
                }
              >
                <AlertTitle>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                  >
                    <IconSparkles />
                    <Typography variant="h5">
                      Create Part with Ailana
                    </Typography>
                    <Chip label="BETA" size="small" color="primary" />
                  </Stack>
                </AlertTitle>
                {watchLink
                  ? 'Click AI Analyze to automatically complete this part record with Ailana. Please be patient as this may take several seconds.'
                  : 'Enter a Link to automatically complete this part record with Ailana'}
              </Alert>
            )}
          {isLoadingScrapeOrgPart && showLink && (
            <LoadingAlert
              variant="outlined"
              severity="info"
              title="Analyzing with Ailana"
            >
              {!!jobMessage && jobMessage}
            </LoadingAlert>
          )}
          {showLink && <PaddedDivider />}
          <HookFormMultiQuerySingleFieldSearchSelect<
            PublicManufacturerSchema,
            OrgSupplier
          >
            name={OrgPartFormConstants.mfg.id}
            label={OrgPartFormConstants.mfg.label}
            control={control}
            errors={errors}
            freeSolo
            fieldName="name"
            mb={0}
            showAiIcon={_.includes(
              watchScrapedFields,
              OrgPartFormConstants.mfg.id
            )}
            additionalOnBlur={handleSearchBlur}
            queryObjects={[
              {
                useGetQuery: useGetPublicManufacturersQuery,
                schema: [],
                searchSchema: ['name'],
              },
              {
                useGetQuery: useGetOrgSuppliersServerSideQuery,
                schema: ['name'],
                searchSchema: [ALL],
              },
            ]}
          />
          <HookFormInput
            control={control}
            errors={errors}
            name={OrgPartFormConstants.mpn.id}
            label={OrgPartFormConstants.mpn.label}
            boxSx={{ mb: 0 }}
            showAiIcon={_.includes(
              watchScrapedFields,
              OrgPartFormConstants.mpn.id
            )}
            additionalOnBlur={handleSearchBlur}
          />
          {isManualEntry && (
            <HookFormInput
              control={control}
              errors={errors}
              name={OrgPartFormConstants.description.id}
              label={OrgPartFormConstants.description.label}
              boxSx={{ mb: 0 }}
              showAiIcon={_.includes(
                watchScrapedFields,
                OrgPartFormConstants.description.id
              )}
            />
          )}
        </Stack>
      </GridLegacy>
    </GridLegacy>
  );
};

export default PartDetailsForm;
